/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: prop-types library.
import { HiChevronDown, HiMagnifyingGlass } from 'react-icons/hi2' // Npm: React icons.
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import InfiniteScroll from 'components/InfiniteScroll'


/*
 * HELPERS
 */
import { createPagination } from 'components/InfiniteScroll/index.pagination'


/*
 * CONSTANTS
 */
const _SEARCH_DEBOUNCE_MS = 800
const _OPTION_HEIGHT = 30;
const _GetOptionValue = option => `${option.displayName} (${option.id})`
const _GetSelectedLabel = value => value


/*
 * OBJECTS
 */
const Index = ({
  name,
  label,
  value,
  data,
  onChange,
  disabled,
  isRequired,
  isInvalid,
  error,
  loadOptions,
  ariaLabel = 'Options',
  searchPlaceholder = 'Search options',
  emptyMessage = 'No options found.',
  getOptionValue = _GetOptionValue,
  getOptionLabel = getOptionValue,
  getSelectedLabel = _GetSelectedLabel,
  placeholder = 'Select option',
  height = '40px'
}) => {
  // Hook assignment.
  const [_isOpen, _setIsOpen] = React.useState(false)
  const [_search, _setSearch] = React.useState('')
  const [_selected, _setSelected] = React.useState(value ?? data ?? '')
  const [_active, _setActive] = React.useState(-1)
  const [_page, _setPage] = React.useState({
    'items': [],
    'loading': false,
    'hasMore': true,
    'error': null
  })
  const _inputRef = React.useRef(null)
  const _infiniteScrollRef = React.useRef(null)
  const _paginationRef = React.useRef(null)
  const _searchTimerRef = React.useRef(null)
  const _listId = React.useId()
  const _scrollId = `${_listId.replace(/:/g, '')}-scroll`

  // Update selected value when the form is reset externally.
  React.useEffect(() => _setSelected(value ?? data ?? ''), [value, data])

  // Create one pagination controller for this selector instance.
  React.useEffect(() => {
    const _Pagination = createPagination(loadOptions, _setPage)

    _paginationRef.current = _Pagination

    // Cleanup controller and queued search.
    return () => {
      clearTimeout(_searchTimerRef.current)
      _Pagination.dispose()
      _paginationRef.current = null
    }
  }, [loadOptions])

  // Queue a single server search after typing stops.
  const _QueueSearch = React.useCallback((search, delay = _SEARCH_DEBOUNCE_MS) => {
    clearTimeout(_searchTimerRef.current)
    _paginationRef.current?.invalidate()
    _searchTimerRef.current = setTimeout(() => {
      _setActive(-1)
      _infiniteScrollRef.current?.scrollToTop()
      _paginationRef.current?.reset(search)
    }, delay)
  }, [])

  // Close dropdown and cancel any queued or active result set.
  const _Close = React.useCallback(() => {
    clearTimeout(_searchTimerRef.current)
    _paginationRef.current?.invalidate()
    _setActive(-1)
    _setIsOpen(false)
  }, [])

  // Open dropdown with the first page of options.
  const _Open = React.useCallback(() => {
    _setSearch('')
    _setActive(-1)
    _setIsOpen(true)
    _QueueSearch('', 0)
  }, [_QueueSearch])

  // Load the next server page through the shared infinite-scroll component.
  const _LoadNextPage = React.useCallback(() => _paginationRef.current?.loadMore(), [])

  // Select option and preserve the consumer's form value format.
  const _Select = React.useCallback(option => {
    const _Value = getOptionValue(option)

    _setSelected(_Value)
    onChange({ 'target': { name, 'value': _Value } })
    _Close()
  }, [_Close, getOptionValue, name, onChange])

  // Handle keyboard navigation inside the virtual list.
  const _HandleKeyDown = React.useCallback(event => {
    if ('Escape' === event.key) {
      event.preventDefault()
      return _Close()
    }

    if ('Enter' === event.key) {
      event.preventDefault()
      if (_page.items[_active]) _Select(_page.items[_active])
      return void 0
    }

    if ('ArrowDown' !== event.key && 'ArrowUp' !== event.key) return void 0
    event.preventDefault()

    const _Direction = 'ArrowDown' === event.key ? 1 : -1
    const _Next = Math.max(0, Math.min(_page.items.length - 1, _active + _Direction))

    _setActive(_Next)
    _infiniteScrollRef.current?.scrollAround(_Next)
    if (_Next === _page.items.length - 1) _infiniteScrollRef.current?.loadMore()
    return void 0
  }, [_Close, _Select, _active, _page.items])

  // Render only rows visible inside the dropdown viewport.
  const _RenderOption = React.useCallback((index, key) => {
    const _Option = _page.items[index]
    const _Value = getOptionValue(_Option)
    const _IsHighlighted = _active === index || _selected === _Value

    return (
      <Box
        as='button'
        type='button'
        role='option'
        tabIndex={-1}
        id={`${_listId}-${index}`}
        key={key}
        aria-selected={_selected === _Value}
        w='100%'
        maxW='100%'
        minW={0}
        h={`${_OPTION_HEIGHT}px`}
        display='flex'
        alignItems='center'
        textAlign='left'
        px={3}
        borderRadius='10px'
        fontFamily='inherit'
        fontSize='15px'
        fontWeight={400}
        lineHeight='20px'
        color='#2B3674'
        overflow='hidden'
        // bg={_IsHighlighted ? '#d36f50' : 'transparent'}
        // _hover={{ 'bg': '#F4511E', 'color': 'white' }}
        bg={_active === index || _selected === _Value ? 'gray.100' : 'transparent'}
        _hover={{ 'bg': 'gray.100' }}
        onMouseDown={event => event.preventDefault()}
        onClick={() => _Select(_Option)}>
        <Text isTruncated>{getOptionLabel(_Option)}</Text>
      </Box>
    )
  }, [_Select, _active, _listId, _page.items, _selected, getOptionLabel, getOptionValue])

  // Return component.
  return (
    <FormControl w='100%' minW={0} fontFamily='inherit' isRequired={isRequired} isInvalid={isInvalid}>
      {label ? (
        <FormLabel w='inherit' display='flex'>
          <Text color='#2D3748' fontFamily='inherit' fontSize='15px' fontWeight={500} lineHeight='20px'>{label}</Text>
        </FormLabel>
      ) : void 0}
      <Popover isOpen={_isOpen && !disabled} onClose={_Close} initialFocusRef={_inputRef} placement='bottom-start' matchWidth>
        <PopoverTrigger>
          <Button
            type='button'
            w='100%'
            maxW='100%'
            minW={0}
            minH={height}
            px={4}
            justifyContent='space-between'
            overflow='hidden'
            border='none'
            borderRadius='12px'
            bg='gray.100'
            color='#2B3674'
            fontFamily='inherit'
            fontSize='15px'
            fontWeight='normal'
            lineHeight='20px'
            boxShadow={isInvalid ? '0 0 0 1.5px #EE5D50' : void 0}
            _hover={{ 'bg': 'gray.100' }}
            _active={{ 'bg': 'gray.100' }}
            _focus={{ 'boxShadow': isInvalid ? '0 0 0 1.5px #EE5D50' : 'none' }}
            _focusVisible={{ 'boxShadow': isInvalid ? '0 0 0 1.5px #EE5D50' : 'none' }}
            isDisabled={disabled}
            aria-label={label || ariaLabel}
            aria-haspopup='listbox'
            aria-expanded={_isOpen && !disabled}
            onClick={() => _isOpen ? _Close() : _Open()}>
            <Text as='span' minW={0} flex={1} textAlign='left' isTruncated>
              {_selected ? getSelectedLabel(_selected) : placeholder}
            </Text>
            <Box as={HiChevronDown} aria-hidden='true' color='#2B3674' flexShrink={0} ml={2} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          w='100%'
          maxW='none'
          zIndex={10000}
          overflow='hidden'
          border='1px solid'
          borderColor='gray.300'
          borderRadius='12px'
          boxShadow='0 18px 40px rgba(112, 144, 176, 0.18)'
          _focus={{ 'outline': 'none', 'boxShadow': '0 18px 40px rgba(112, 144, 176, 0.18)' }}
          _focusVisible={{ 'outline': 'none', 'boxShadow': '0 18px 40px rgba(112, 144, 176, 0.18)' }}>
          <PopoverBody minW={0} p={2} bg='white' fontFamily='inherit'>
            <InputGroup>
              <InputLeftElement pointerEvents='none' w='40px' h='40px'>
                <Box as={HiMagnifyingGlass} color='#A3AED0' boxSize='17px' />
              </InputLeftElement>
              <Input
                ref={_inputRef}
                value={_search}
                h='40px'
                pl='40px'
                pr={3}
                py={0}
                role='combobox'
                aria-label={searchPlaceholder}
                aria-autocomplete='list'
                aria-expanded={_isOpen}
                aria-controls={_scrollId}
                aria-activedescendant={0 <= _active && _page.items[_active] ? `${_listId}-${_active}` : void 0}
                placeholder={searchPlaceholder}
                border='none'
                borderRadius='12px'
                bg='gray.100'
                color='#2B3674'
                fontFamily='inherit'
                fontSize='15px'
                fontWeight={400}
                lineHeight='20px'
                _placeholder={{ 'color': '#A3AED0' }}
                _focus={{ 'borderColor': 'transparent', 'boxShadow': 'none' }}
                _focusVisible={{ 'borderColor': 'transparent', 'boxShadow': 'none' }}
                onKeyDown={_HandleKeyDown}
                onChange={event => {
                  const _Value = event.target.value

                  _setSearch(_Value)
                  _QueueSearch(_Value)
                }} />
            </InputGroup>
            <InfiniteScroll
              ref={_infiniteScrollRef}
              id={_scrollId}
              items={_page.items}
              hasMore={_page.hasMore}
              loading={_page.loading}
              error={_page.error}
              loadMore={_LoadNextPage}
              renderItem={_RenderOption}
              ariaLabel={ariaLabel}
              height={260}
              itemHeight={_OPTION_HEIGHT} />
            <Box aria-live='polite' minH='24px' px={2} py={1} color='#A3AED0' fontFamily='inherit' fontSize='13px' fontWeight={400}>
              {_page.error || (!_page.loading && !_page.items.length ? (_page.hasMore ? 'Searching...' : emptyMessage) : '')}
            </Box>
            {!_page.loading && _page.error ? (
              <Button type='button' size='sm' variant='ghost' w='100%' color='#2B3674' fontWeight={500} onClick={() => _paginationRef.current?.loadMore()}>
                Retry
              </Button>
            ) : void 0}
          </PopoverBody>
        </PopoverContent>
      </Popover>
      {isInvalid && error ? <FormErrorMessage>{error}</FormErrorMessage> : void 0}
    </FormControl>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'name': PropTypes.string.isRequired,
  'label': PropTypes.string,
  'value': PropTypes.string,
  'data': PropTypes.string,
  'onChange': PropTypes.func.isRequired,
  'disabled': PropTypes.bool,
  'isRequired': PropTypes.bool,
  'isInvalid': PropTypes.bool,
  'error': PropTypes.string,
  'loadOptions': PropTypes.func.isRequired,
  'ariaLabel': PropTypes.string,
  'searchPlaceholder': PropTypes.string,
  'emptyMessage': PropTypes.string,
  'getOptionValue': PropTypes.func,
  'getOptionLabel': PropTypes.func,
  'getSelectedLabel': PropTypes.func,
  'placeholder': PropTypes.string,
  'height': PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}


/*
 * EXPORT
 */
export default React.memo(Index)
